import React, { useEffect, useState } from 'react';
import { submitFormData } from '../../../../pages/api';

const ShopFloor = ({ formData, onFormDataChange }) => {
    const { shopFloorData = [], observations = [], nonConformance = '' } = formData;

    useEffect(() => {
        // Destructure formData to get initial values
        const { shopFloorData = [], observations = [], nonConformance = '' } = formData;
    
        // Check if formData already has prefilled data
        const hasFormData = shopFloorData.length > 0 || observations.length > 0 || nonConformance.trim() !== '';
    
        if (!hasFormData) {
            // If no formData, check metadata and inspectionData
            const metadata = JSON.parse(sessionStorage.getItem('metadata'));
            const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));
    
            let prefilledShopFloorData = [];
            let prefilledObservations = [];
            let prefilledNonConformance = '';
    
            // Check metadata for prefilled data
            if (metadata && metadata.pre && metadata.pre.shopFloor_pre) {
                prefilledShopFloorData = metadata.pre.shopFloor_pre;
                prefilledObservations = prefilledShopFloorData.map(() => ({ observation1: '', observation2: '' }));
            }
    
            // Check inspectionData for prefilled data (overrides metadata if present)
            if (
                inspectionData &&
                inspectionData.PreLamination &&
                inspectionData.PreLamination.shopFloor_pre
            ) {
                const { shopfloor, nonConformance: inspectionNonConformance } =
                    inspectionData.PreLamination.shopFloor_pre;
    
                prefilledShopFloorData = shopfloor;
                prefilledObservations = shopfloor.map((item) => ({
                    observation1: item.observation1 || '',  
                    observation2: item.observation2 || '',
                }));
                prefilledNonConformance = inspectionNonConformance || '';
            }
    
            // Update formData only if prefilled data exists
            if (prefilledShopFloorData.length > 0 || prefilledObservations.length > 0 || prefilledNonConformance.trim() !== '') {
                onFormDataChange({
                    ...formData,
                    shopFloorData: prefilledShopFloorData,
                    observations: prefilledObservations,
                    nonConformance: prefilledNonConformance,
                });
            }
        }
    }, [formData, onFormDataChange]);
    
    const handleObservationChange = (index, field, value) => {
        const updatedObservations = [...observations];
        updatedObservations[index][field] = value;
        onFormDataChange({ ...formData, observations: updatedObservations });
    };

    const handleNonConformanceChange = (e) => {
        onFormDataChange({ ...formData, nonConformance: e.target.value });
    };

    const submitObservations = async () => {
        const incompleteObservation = observations.some(obs => !obs.observation1.trim());
        if (incompleteObservation) {
            alert('Please fill all Observation 1 fields before submitting.');
            return;
        }
        try {
            const projectId = localStorage.getItem('selectedProject');
            const inspectionDate = sessionStorage.getItem('inspectionDate');
            const inspectionShift = sessionStorage.getItem('inspectionShift');
            const inspectorName = sessionStorage.getItem('username');
            const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');
            const combinedData = shopFloorData.map((item, index) => ({
                product: item.product,
                specifications: item.specifications,
                frequency: item.frequency,
                observation1: observations[index]?.observation1 || '',
                observation2: observations[index]?.observation2 || '',
            }));
            const payload = {
                projectId,
                inspectionSupplier,
                inspectionDate,
                inspectionShift,
                inspectorName,
                status: 'completed',
                PreLamination: {
                    shopFloor_pre: { shopfloor: combinedData, nonConformance: nonConformance },
                }
            };
            await submitFormData(payload);
            alert('Observations submitted successfully!');
        } catch (error) {
            console.error('Error submitting observations:', error);
            alert('Failed to submit observations.');
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Shop Floor Inspection</h3>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shopFloorData.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b last:border-none ${
                                    index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                                } hover:bg-gray-100 transition-colors duration-200`}
                            >
                                <td className="py-3 px-4">{item.product}</td>
                                <td className="py-3 px-4">{item.specifications}</td>
                                <td className="py-3 px-4">{item.frequency}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={observations[index]?.observation1 || ''}
                                        onChange={(e) =>
                                            handleObservationChange(index, 'observation1', e.target.value)
                                        }
                                        placeholder="Enter observation 1"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={observations[index]?.observation2 || ''}
                                        onChange={(e) =>
                                            handleObservationChange(index, 'observation2', e.target.value)
                                        }
                                        placeholder="Enter observation 2"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="mt-6">
                <label className="block text-gray-700 font-semibold mb-2" htmlFor="nonConformance">
                    Non Conformance
                </label>
                <textarea
                    value={nonConformance}
                    onChange={handleNonConformanceChange}
                    placeholder="Enter details about non conformance..."
                    className="w-full border border-gray-300 p-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                    rows="4"
                />
            </div>
            <div className="mt-6">
                <button
                    onClick={submitObservations}
                    className="px-6 py-3 bg-[#424752] text-white font-semibold rounded-lg hover:bg-gray-500"
                >
                    Submit
                </button>
            </div>
        </div>
    );
};

export default ShopFloor;